import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import useTerrainSettings from '../../hooks/useTerrainSettings';
import useDoiSettings from '../../hooks/useDoiSettings';
import { useFenceScale } from '../FenceColour';
import { fencePixels } from '../../lib/fence-texture';
import { makeDoiMask } from '../../lib/inversion-results';
import { terrainApi } from '../../lib/terrain-api';
import { getTileAuthToken, rasterTileUrl, startTileAuth } from '../../lib/raster-api';
import { BASEMAP_TILE_SOURCES } from '../../lib/map-styles';
import { run } from '../../lib/worker-pool';
import VoxelModel, { useVoxelSelection } from '../VoxelModel';
import VolumeSlices, { useSliceContext } from '../VolumeSlices';
import { columnSurface } from '../../lib/volume-slices';
import { localToLonLat } from '../../lib/view-sync';
import {
  DEFAULT_GRID, drapedFenceGeometry, elevationAt, drapedVoxelPositions, gridLonLats, gridSpec, rangeTiles, sceneExtent, tileRangeFor, tileUrl, toLocal,
} from '../../lib/terrain-mesh';

/**
 * 3D terrain draping (docs/DEM_TERRAIN.md, section 6). Rendered inside the viewer's
 * <Canvas> through SubsurfaceViewer3D's `sceneExtras({ origin })`.
 *
 * When the project has a DEM (lib/terrain-settings.js): samples the DEM on an N x N grid
 * over the scene extent (POST /dem/sample, spacing = grid cell), builds the mesh in the
 * geo worker (task `terrainMesh`), drapes the hillshade or elevation tiles as a texture,
 * and redraws the fences and voxel clouds hung from the ground (DEM, or the model's own
 * surface when it has topography). Everything sits in one group whose y-scale is the
 * vertical exaggeration. `renderDraped({ groundY, refElev })` draws extra nodes inside that
 * group (boreholes hung at their collar elevation); `fallback` is drawn instead while
 * there is no terrain. `onReady(bool)` tells the page whether the draped versions are
 * drawn, so it can hide the flat ones (SubsurfaceViewer3D `terrainDraped`).
 */

/** Stations x depth-levels colour texture (same colour table and DOI mask as the flat FencePlane). */
function fenceTexture(entry, doi, scale) {
  const cells = entry.cells || [];
  const belowDoi = makeDoiMask(cells, { mask: doi.mask, threshold: doi.threshold, fallbackDoi: entry.model?.doi });
  const { w, h, data } = fencePixels(cells, scale, belowDoi);
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  tex.needsUpdate = true;
  tex.magFilter = THREE.NearestFilter;
  return tex;
}

function DrapedFence({ entry, grid, origin, refElev, opacity, clippingPlanes }) {
  const doi = useDoiSettings();
  const scale = useFenceScale(entry);
  const built = useMemo(() => {
    const g = drapedFenceGeometry(entry, grid, origin, refElev);
    if (!g) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(g.positions, 3));
    geom.setAttribute('uv', new THREE.BufferAttribute(g.uvs, 2));
    geom.setIndex(new THREE.BufferAttribute(g.indices, 1));
    return { geom, tex: fenceTexture(entry, { mask: doi.mask, threshold: doi.threshold }, scale) };
  }, [entry, grid, origin, refElev, doi.mask, doi.threshold, scale]);
  useEffect(() => () => { built?.geom.dispose(); built?.tex.dispose(); }, [built]);
  if (!built) return null;
  return (
    <mesh geometry={built.geom}>
      <meshBasicMaterial map={built.tex} side={THREE.DoubleSide} transparent opacity={opacity} clippingPlanes={clippingPlanes} />
    </mesh>
  );
}

function DrapedVoxels({ entry, grid, origin, refElev, opacity, clippingPlanes }) {
  const { sel, outsideOpacity } = useVoxelSelection(entry.id);
  const positions = useMemo(() => (sel ? drapedVoxelPositions(sel.cells, grid, origin, refElev) : null), [sel, grid, origin, refElev]);
  if (!sel) return null;
  return <VoxelModel entry={entry} sel={sel} positions={positions} opacity={opacity} outsideOpacity={outsideOpacity} clippingPlanes={clippingPlanes} />;
}

/**
 * The slices of the viewer (components/VolumeSlices.js) hung from the ground: the lib/volume-slices raster is
 * sampled by depth, each column is placed at ground - depth (the model's own surface, else the DEM), the same
 * rule as DrapedVoxels. A horizontal (Z) slice is therefore a constant-depth surface that follows the ground.
 * Picks report the block's elevation (ground - depth).
 */
function DrapedSlices({ ctx, grid, origin, refElev }) {
  const { items, sel, frame, grid: sliceGrid, extent, outsideOpacity, clippingPlanes, probe, onPick, onDrag } = ctx;
  const draped = useMemo(() => {
    const surf = columnSurface(sel.cells, sliceGrid);
    const ground = (x, y, sx, sz) => {
      const s = surf ? surf(x, y) : null;
      if (s !== null) return s;
      const [lon, lat] = localToLonLat(sx, sz, origin);
      const g = elevationAt(grid, lon, lat);
      return g === null || g === undefined ? refElev : g;
    };
    const map = (x, y, d) => {
      const p = frame.map(x, y, d);
      return [p[0], ground(x, y, p[0], p[2]) - d - refElev, p[2]];
    };
    const elevationOf = (c) => { const p = frame.map(c.x, c.y, 0); return ground(c.x, c.y, p[0], p[2]) - c.depth; };
    return { ...frame, draped: true, map, elevationOf };
  }, [sel, sliceGrid, frame, grid, origin, refElev]);
  const pick = useCallback((cell, point) => onPick({ ...cell, z: draped.elevationOf(cell) }, point), [onPick, draped]);
  return (
    <VolumeSlices items={items} sel={sel} frame={draped} extent={extent} outsideOpacity={outsideOpacity}
      clippingPlanes={clippingPlanes} probe={probe} onPick={pick} onDrag={onDrag} />
  );
}

/** Fetch the tiles of a range and compose them on a canvas. DEM tiles need the bearer token; basemap tiles are plain CORS fetches (a failed tile stays blank). */
async function composeTexture(template, range, { token = null } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = 256 * range.nx;
  canvas.height = 256 * range.ny;
  const ctx = canvas.getContext('2d');
  await Promise.all(rangeTiles(range).map(async (t) => {
    try {
      const res = await fetch(tileUrl(template, t), token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (!res.ok) return;
      const bmp = await createImageBitmap(await res.blob());
      ctx.drawImage(bmp, t.col * 256, t.row * 256);
    } catch { /* leave the tile blank */ }
  }));
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

async function demToken() {
  const { supabase } = await import('../../lib/supabase');
  await startTileAuth(supabase);
  return getTileAuthToken();
}

const FLAT_GRID = 16;

export default function TerrainMesh({ origin, models = [], opacity = 0.95, clippingPlanes = [], showModels = true, showSections = true, showVolumes = true, onReady, onInfo,
  fallback = null, renderDraped = null }) {
  const terrain = useTerrainSettings();
  const slicing = useSliceContext();
  const basemap = BASEMAP_TILE_SOURCES[terrain.drapeStyle] || null;
  const demId = terrain.drape3d ? terrain.demDatasetId : null;
  // a flat ground image (no DEM) is drawn only when a basemap texture is chosen
  const flat = terrain.drape3d && !terrain.demDatasetId && !!basemap;
  const kind = basemap ? 'basemap' : 'dem';
  const version = terrain.dem?.job_id || '';
  const placed = useMemo(() => models.filter((m) => (m.line?.length === 2 && m.cells?.length)
    || m.cells?.[0]?.lon !== undefined), [models]);
  const extent = useMemo(() => ((demId || flat) && origin ? sceneExtent(placed) : null), [demId, flat, origin, placed]);
  const [mesh, setMesh] = useState(null);
  const [texture, setTexture] = useState(null);

  useEffect(() => {
    if (!extent || !(demId || flat)) { setMesh(null); return undefined; }
    let active = true;
    // the uvs use the tile range of the texture kind, so the mesh is rebuilt when the kind changes
    const tiles = tileRangeFor(extent, kind);
    const build = (elevations, n, refElev) => run('terrainMesh', { elevations, n, extent, origin, tiles, refElev }, { label: 'Terrain mesh' })
      .then((m) => { if (active) setMesh(m ? { ...m, n, extent, tiles, flat, kind } : null); });
    let job;
    if (flat) {
      job = build(new Array(FLAT_GRID * FLAT_GRID).fill(0), FLAT_GRID, 0);
    } else {
      const { n, spacing } = gridSpec(extent, DEFAULT_GRID);
      job = terrainApi.sample(demId, gridLonLats(extent, n), spacing).then((r) => build(r.data.elevations, n));
    }
    job.catch(() => { if (active) setMesh(null); });
    return () => { active = false; };
  }, [extent, demId, flat, kind, origin]);

  useEffect(() => {
    // No source for a texture (the DEM was just switched off while the old mesh is still in state,
    // or no basemap texture is chosen): do not request `datasets/null/raster/tiles/...`.
    if (!mesh || mesh.kind !== (basemap ? 'basemap' : 'dem') || (!basemap && !demId)) { setTexture(null); return undefined; }
    let active = true;
    let made = null;
    const compose = basemap
      ? composeTexture(basemap.template, mesh.tiles)
      : demToken().then((token) => composeTexture(
        rasterTileUrl(demId, { style: terrain.drapeStyle === 'elevation' ? 'elevation' : 'hillshade', v: version }), mesh.tiles, { token }));
    compose
      .then((tex) => { made = tex; if (active) setTexture(tex); else tex.dispose(); })
      .catch(() => { if (active) setTexture(null); });
    return () => { active = false; if (made) made.dispose(); };
  }, [mesh, demId, basemap, terrain.drapeStyle, version]);

  const geometry = useMemo(() => {
    if (!mesh) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(mesh.normals, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(mesh.uvs, 2));
    g.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
    return g;
  }, [mesh]);
  useEffect(() => () => geometry?.dispose(), [geometry]);

  const ready = !!geometry && !mesh?.flat;
  useEffect(() => { if (onReady) onReady(ready); }, [ready, onReady]);
  useEffect(() => () => { if (onReady) onReady(false); }, [onReady]);

  const { grid, groundY } = useMemo(() => {
    if (!mesh) return {};
    const g0 = { values: mesh.filled, n: mesh.n, extent: mesh.extent };
    // y of a ground point in this group (before VE): the collar elevation when known, else the DEM
    const gy = (lon, lat, collar) => {
      const g = Number.isFinite(collar) ? collar : elevationAt(g0, lon, lat);
      return g === null || g === undefined ? 0 : g - mesh.refElev;
    };
    return { grid: g0, groundY: gy };
  }, [mesh]);

  // ground range under the drawn models and the mesh's plan extent, for the camera presets (lib/camera-framing drapedBox)
  const info = useMemo(() => {
    if (!mesh || mesh.flat || !grid) return null;
    let lo = Infinity, hi = -Infinity;
    const take = (lon, lat) => {
      const g = elevationAt(grid, lon, lat);
      if (Number.isFinite(g)) { if (g < lo) lo = g; if (g > hi) hi = g; }
    };
    placed.forEach((m) => {
      if (m.line?.length === 2) {
        for (let i = 0; i <= 32; i++) take(m.line[0][0] + ((m.line[1][0] - m.line[0][0]) * i) / 32, m.line[0][1] + ((m.line[1][1] - m.line[0][1]) * i) / 32);
      } else {
        const step = Math.max(1, Math.floor(m.cells.length / 2000));
        for (let i = 0; i < m.cells.length; i += step) take(m.cells[i].lon, m.cells[i].lat);
      }
    });
    if (!Number.isFinite(lo)) return null;
    const e = mesh.extent;
    const [x0, z0] = toLocal(e.west, e.south, origin);
    const [x1, z1] = toLocal(e.east, e.north, origin);
    return { refElev: mesh.refElev, minElev: lo, maxElev: hi, plan: { min: [Math.min(x0, x1), Math.min(z0, z1)], max: [Math.max(x0, x1), Math.max(z0, z1)] } };
  }, [mesh, grid, placed, origin]);
  useEffect(() => { if (onInfo) onInfo(info); }, [info, onInfo]);
  useEffect(() => () => { if (onInfo) onInfo(null); }, [onInfo]);

  if (!geometry) return fallback;
  if (mesh.flat) {
    return (
      <>
        <group scale={[1, Math.max(0.1, Number(terrain.ve3d) || 1), 1]} name="ground-image">
          <mesh geometry={geometry}>
            <meshBasicMaterial key={texture ? 'tex' : 'flat'} map={texture || null} color={texture ? '#ffffff' : '#8b9a7a'} side={THREE.DoubleSide}
              transparent opacity={terrain.surfaceOpacity3d} />
          </mesh>
        </group>
        {fallback}
      </>
    );
  }
  const fences = !showModels || !showSections ? [] : placed.filter((m) => m.line?.length === 2 && !['grid', 'mesh3d'].includes(m.model?.geometryKind));
  const voxels = !showModels || !showVolumes ? [] : placed.filter((m) => ['grid', 'mesh3d'].includes(m.model?.geometryKind) && m.id !== slicing?.hideModelId);
  return (
    <group scale={[1, Math.max(0.1, Number(terrain.ve3d) || 1), 1]} name="terrain">
      <mesh geometry={geometry} receiveShadow>
        <meshLambertMaterial key={texture ? 'tex' : 'flat'} map={texture || null} color={texture ? '#ffffff' : '#8b9a7a'}
          side={THREE.DoubleSide} transparent opacity={terrain.surfaceOpacity3d} />
      </mesh>
      {fences.map((f) => (
        <DrapedFence key={`dfence-${f.id}`} entry={f} grid={grid} origin={origin} refElev={mesh.refElev}
          opacity={opacity} clippingPlanes={clippingPlanes} />
      ))}
      {voxels.map((f) => (
        <DrapedVoxels key={`dvox-${f.id}`} entry={f} grid={grid} origin={origin} refElev={mesh.refElev}
          opacity={opacity} clippingPlanes={clippingPlanes} />
      ))}
      {slicing && showModels && showVolumes && <DrapedSlices ctx={slicing} grid={grid} origin={origin} refElev={mesh.refElev} />}
      {renderDraped && renderDraped({ groundY, refElev: mesh.refElev })}
    </group>
  );
}
